import React, { RefObject } from 'react';
import i18n from 'i18n-js';

import { font } from '@styles/fonts';

import { Events, TimelineDataWithLoadingState, TriggerHidePopup, TriggerShowPopup } from '@interfaces/timeline';

import { LegendRules } from '@components/machines/components/timeline/calculations/index';
import { Popup } from '@components/machines/components/timeline';
import Svg from '@styles/icons/svg';
import { TimelineBlockTitle } from './TimelineBlockTitle';
import { TIMELINE_CONFIG, TIMELINE_LINE_CONFIG, TIMELINE_UNDERLINE_TEXT_CONFIG } from '@components/machines/components/timeline/constants';
import TimelineShimmer from './TimelineShimmer';
import {
  calculateTimelineBatches,
  getTimelineFontStyles,
  getTimelineElementBorderRadius,
  NC_FILE_TYPE_LABELS,
  matchNcFileIcon,
  matchNcFileColor,
} from '@components/machines/components/timeline/helpers';
import { ColorFunction } from '@styles/colors/types';
import { ProductionBatch } from '@interfaces/search';

export default (props: {
  C: ColorFunction;
  triggerShowPopup: TriggerShowPopup;
  triggerHidePopup: TriggerHidePopup;
  batches: TimelineDataWithLoadingState<ProductionBatch[]>;
  blockWidth: number;
  rules: LegendRules;
  timelineRef: RefObject<HTMLDivElement>;
  startTime: string;
  events: Events;
}) => {
  const { C, triggerShowPopup, triggerHidePopup, batches, blockWidth, rules, timelineRef, startTime, events } = props;

  const { fontStylesWeb } = getTimelineFontStyles();

  const { batchElements } = calculateTimelineBatches({
    batches: batches.data,
    secondWidth: rules.secondWidth,
    timelineStart: startTime,
    batchStartField: 'startedOn',
    batchEndField: 'endedOn',
    groupedByBatchEvents: events.groupedByBatch,
  });

  const { backgroundColor: timelineBg, borderRadius: timelineBorderRadius, height: timelineHeight } = TIMELINE_LINE_CONFIG;

  return (
    <div>
      <TimelineBlockTitle C={C} containerStyle={{ marginBottom: 10, marginTop: TIMELINE_CONFIG.gapBetweenBlocks }}>
        {i18n.t('NC File Type')}
      </TimelineBlockTitle>
      {Object.keys(NC_FILE_TYPE_LABELS).map((type) => (
        <div style={{ marginBottom: 8 }} key={type}>
          <div
            style={{
              ...font['Subhead 2'],
              color: C(TIMELINE_UNDERLINE_TEXT_CONFIG.color),
              lineHeight: '20px',
              width: 'min-content',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Svg icon={matchNcFileIcon(type)} width={16} height={16} style={{ display: 'inline-block' }} />
            {i18n.t(NC_FILE_TYPE_LABELS[type as 'Unknown' | 'Linked' | 'Change' | 'Ignored'])}
          </div>
          <div
            style={{
              width: blockWidth,
              height: timelineHeight + TIMELINE_UNDERLINE_TEXT_CONFIG.height,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {batches.loaded && events.loaded ? (
              <>
                <div
                  style={{
                    borderRadius: timelineBorderRadius,
                    backgroundColor: C(timelineBg),
                    height: timelineHeight,
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                />
                <div style={{ position: 'relative', height: TIMELINE_UNDERLINE_TEXT_CONFIG.height, overflow: 'hidden' }} />
                {batchElements?.map(({ b, elementLeft, elementWidth }, index) => {
                  const display = b?.ncFileStatus === type && b?.productionBatchId != null;
                  const shouldDisplayText = display && elementWidth > 5;

                  const borderRadiusStyles = getTimelineElementBorderRadius({
                    elementLeft,
                    elementWidth,
                    blockWidth,
                    borderRadius: timelineBorderRadius,
                  });

                  return (
                    <React.Fragment key={b ? `${b?.startedOn}-${b?.endedOn}-nc-file-type-${type}-${index}` : index}>
                      <div
                        style={{
                          ...borderRadiusStyles,
                          position: 'absolute',
                          left: elementLeft,
                          width: elementWidth,
                          height: timelineHeight,
                          top: 0,
                          backgroundColor: display && !!b ? C(matchNcFileColor(type, b?.ncFileStatus)) : 'transparent',
                          overflow: 'hidden',
                        }}
                      />
                      {shouldDisplayText && (
                        <div
                          style={{
                            left: elementLeft,
                            width: elementWidth,
                            position: 'absolute',
                            top: timelineHeight,
                            overflow: 'hidden',
                            display: 'flex',
                          }}
                          onMouseOver={(event) => {
                            const timelineRect = timelineRef?.current?.getBoundingClientRect();
                            if (timelineRect) {
                              const el: any = event.target;
                              const width = elementWidth;
                              const rect = el.getBoundingClientRect();
                              const position = {
                                top: rect.top - timelineRect.top - 10,
                                left: rect.left - timelineRect.left,
                              };

                              triggerShowPopup(
                                {
                                  show: true,
                                  item: { ...b, ncFileMain: b?.ncFileName },
                                  width,
                                  position,
                                },
                                'ncFileName'
                              );
                            }
                          }}
                          onMouseOut={() => {
                            triggerHidePopup('ncFileName');
                          }}
                        >
                          <div
                            style={{
                              ...fontStylesWeb,
                              color: C(TIMELINE_UNDERLINE_TEXT_CONFIG.color),
                              whiteSpace: 'nowrap',
                              userSelect: 'none',
                              height: TIMELINE_UNDERLINE_TEXT_CONFIG.height,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {b?.ncFileName}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            ) : (
              <TimelineShimmer style={{ height: timelineHeight, borderRadius: timelineBorderRadius }} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
